import { Alert, Button, Select, Spinner, Text } from '@risksmart-app/atomic-ui';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { getOrganization } from '@risksmart-app/components/src/utils/authUtils';
import { RiskScoringModel as Risk_Scoring_Model_Enum } from '@risksmart-app/domain/src/types/consts/risk-scoring-model';
import type { RoundingMode } from '@risksmart-app/domain/src/types/risk-score-breakdown';
import { roundingModeSchema } from '@risksmart-app/domain/src/types/risk-score-breakdown';
import { type FC, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledJsonEditor } from 'src/components/form/controlled-json-editor/ControlledJsonEditor';
import ControlledSelect from 'src/components/form/controlled-select';
import { useRecalculateRiskScores } from 'src/hooks/mutations/aggregation/useRecalculateRiskScores';
import { useGetAggregationSettingsForOrg } from 'src/hooks/queries/aggregation/useGetAggregationSettingsForOrg';
import { useIsCustomerSupport } from 'src/hooks/useIsCustomerSupport';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import { useScoringSettings } from 'src/ratings/useScoringSettings';

import { handleError } from '@/utils/errorUtils';

import { findMatrixGaps } from './matrixGaps';
import { RecalculateRiskScoresDialog } from './RecalculateRiskScoresDialog';
import { useRecalculationIndication } from './useRecalculationIndication';

const DEFAULT_ROUNDING_MODE: RoundingMode = 'nearest';

// 'none' is displayed when stored but never offered. The band rule that makes
// it safe is in place; what is not met is the rest of RSP-7297's gate, which
// lifts this — see `_dev/continuous-impact-likelihood/RSP-7297-gate-status.md`.
const SELECTABLE_ROUNDING_MODES = roundingModeSchema.options.filter(
  (mode) => mode !== 'none'
);

const isSelectableRoundingMode = (value: string): value is RoundingMode =>
  SELECTABLE_ROUNDING_MODES.some((mode) => mode === value);

/**
 * Normalise a RiskScoringModelConfig to an object. Accepts the form's JSON
 * string and the persisted `aggregation_org.Config` object. Treat empty
 * strings and invalid JSON as an empty object so the dropdown can still render
 * and write without clobbering existing config — see RSP-5817.
 */
const parseConfigSafely = (raw: unknown): Record<string, unknown> => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string' || raw.trim() === '') {
    return {};
  }
  try {
    return parseConfigSafely(JSON.parse(raw));
  } catch {
    return {};
  }
};

const readRoundingMode = (raw: unknown): RoundingMode => {
  const parsed = roundingModeSchema.safeParse(
    parseConfigSafely(raw).roundingMode
  );

  return parsed.success ? parsed.data : DEFAULT_ROUNDING_MODE;
};

export const RiskScoreSettings: FC = () => {
  const { control, setValue } = useFormContext();
  const { t } = useTranslation('common', { keyPrefix: 'modules.fields' });
  const { user } = useRisksmartUser();
  const organisationName: string | undefined =
    getOrganization(user) || undefined;
  // D3: the mode is gated to the same audience as Recalculate, because a mode
  // nobody can apply is a mode nobody should be able to select. Resolved
  // server-side on V3 and fails closed while loading (see the hook).
  const isCustomerSupport = useIsCustomerSupport();
  const canChangeRoundingMode = isCustomerSupport;
  const { addNotification } = useNotifications();
  // Which leg `useRecalculateRiskScores` takes decides who reports its failure.
  const isTrpcEnabled = useIsFeatureFlagEnabled('trpc');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const riskScoringModel = useWatch({ control, name: 'RiskScoringModel' });
  const riskScoringModelConfig = useWatch({
    control,
    name: 'RiskScoringModelConfig',
  });

  const showRoundingMode =
    riskScoringModel ===
      Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages ||
    riskScoringModel === Risk_Scoring_Model_Enum.RiskAggregationOnly;

  const currentRoundingMode = readRoundingMode(riskScoringModelConfig);

  const roundingModeOptions: { label: string; value: RoundingMode }[] = useMemo(
    () =>
      roundingModeSchema.options.map((mode) => ({
        value: mode,
        label: t(`RiskScoringRoundingModes.${mode}` as const),
      })),
    [t]
  );
  const selectableRoundingModeOptions = roundingModeOptions.filter((option) =>
    isSelectableRoundingMode(option.value)
  );

  // Flag off (or no stored config) yields no configured matrix, so there is
  // nothing to check and no warning.
  const { config: scoringConfig } = useScoringSettings({
    configType: 'risk_assessment',
    skip: !showRoundingMode,
  });
  const matrixGaps = useMemo(
    () => (scoringConfig ? findMatrixGaps(scoringConfig) : []),
    [scoringConfig]
  );

  // The persisted mode, as opposed to the form's possibly-unsaved value: the
  // outstanding indication must react to a save, not to a dropdown pick.
  const { data: aggregationSettings, loading: aggregationSettingsLoading } =
    useGetAggregationSettingsForOrg({
      queryArgs: {},
      shouldSkip: !showRoundingMode,
    });
  const storedRoundingMode =
    aggregationSettingsLoading || !aggregationSettings
      ? undefined
      : readRoundingMode(aggregationSettings.aggregation_org[0]?.Config);

  const handleRoundingModeChange = (nextMode: RoundingMode) => {
    const config = parseConfigSafely(riskScoringModelConfig);
    const next = { ...config, roundingMode: nextMode };
    setValue('RiskScoringModelConfig', JSON.stringify(next, null, 2), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const { recalculateRiskScores, loading: recalculating } =
    useRecalculateRiskScores();
  const {
    indication,
    runMode,
    savedModeAppliesNextRun,
    markStarted,
    markRecalculated,
    markFailed,
  } = useRecalculationIndication({
    storedMode: storedRoundingMode,
    inProgress: recalculating,
  });

  const explanation = [
    t('RiskScoringRoundingMode_inert'),
    t('RiskScoringRoundingMode_who_can_recalculate'),
    ...(canChangeRoundingMode
      ? []
      : [t('RiskScoringRoundingMode_how_to_request')]),
  ].join(' ');

  const modeLabel = (mode: RoundingMode | undefined) =>
    t(`RiskScoringRoundingModes.${mode ?? DEFAULT_ROUNDING_MODE}` as const);

  const indicationCopy = ((): {
    variant: 'active' | 'warning';
    title: string;
    description: string;
  } => {
    if (indication === 'in_progress') {
      return {
        variant: 'active',
        title: t('RiskScoringRoundingMode_recalculation_in_progress_title'),
        description: [
          t('RiskScoringRoundingMode_recalculation_in_progress'),
          ...(savedModeAppliesNextRun
            ? [
                t(
                  'RiskScoringRoundingMode_recalculation_in_progress_mode_changed',
                  {
                    runMode: modeLabel(runMode),
                    mode: modeLabel(storedRoundingMode),
                  }
                ),
              ]
            : []),
        ].join(' '),
      };
    }
    if (indication === 'failed') {
      return {
        variant: 'warning',
        title: t('RiskScoringRoundingMode_recalculation_failed_title'),
        description: t('RiskScoringRoundingMode_recalculation_failed', {
          mode: modeLabel(runMode),
        }),
      };
    }

    return {
      variant: 'active',
      title: t('RiskScoringRoundingMode_recalculation_outstanding_title'),
      description: t('RiskScoringRoundingMode_recalculation_outstanding', {
        mode: modeLabel(storedRoundingMode),
      }),
    };
  })();

  const handleRecalculate = async () => {
    setShowConfirmModal(false);
    // The run scores every tier with the mode stored when it started, whatever
    // is saved while it runs (RSP-7300) — so this is the mode every indication
    // about the run reports.
    const modeAtStart = storedRoundingMode;
    markStarted(modeAtStart);
    try {
      await recalculateRiskScores();
      markRecalculated(modeAtStart);
      addNotification({
        type: 'success',
        content: t('RecalculateRiskScores_success'),
      });
    } catch (error) {
      markFailed(modeAtStart);
      // The Apollo leg is already reported by the error link in
      // `ApolloGraphqlProvider`. The tRPC leg is not: `makeQueryClient`'s
      // reporter covers queries only, deliberately, because `FormContext` owns
      // mutation reporting — and this is a settings button, not a form submit.
      // Without this the operator gets the generic toast and the cause of a
      // failed org-wide recalculation reaches nothing.
      if (isTrpcEnabled) {
        handleError(error, {
          tags: { source: 'trpc-mutation' },
          extra: {
            procedurePath: 'frontend.aggregation.recalculateRiskScores',
            roundingMode: modeAtStart,
          },
        });
      }
      addNotification({
        type: 'error',
        content: t('RecalculateRiskScores_error'),
      });
    }
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <ControlledSelect
        control={control}
        testId={'riskScoringModel'}
        name={'RiskScoringModel'}
        label={t('RiskScoringModel')}
        options={[
          {
            label: t('RiskScoringModels.default'),
            value: Risk_Scoring_Model_Enum.Default,
          },
          {
            label: t('RiskScoringModels.control_effectiveness_averages'),
            value: Risk_Scoring_Model_Enum.ControlEffectivenessAverages,
          },
          {
            label: t('RiskScoringModels.typed_control_effectiveness_averages'),
            value: Risk_Scoring_Model_Enum.TypedControlEffectivenessAverages,
          },
          {
            label: t('RiskScoringModels.risk_aggregation_only'),
            value: Risk_Scoring_Model_Enum.RiskAggregationOnly,
          },
        ]}
      />
      {showRoundingMode && (
        <div
          data-testid={'form-field-risk-scoring-rounding-mode'}
          className={'flex flex-col gap-1'}
        >
          <Text preset={'body-md'}>{t('RiskScoringRoundingMode')}</Text>
          <Text preset={'body-sm'} className={'text-neutral-muted-foreground'}>
            {t('RiskScoringRoundingMode_help')}
          </Text>
          <div data-testid={'risk-scoring-rounding-mode'}>
            <Select
              items={roundingModeOptions}
              value={currentRoundingMode}
              disabled={!canChangeRoundingMode}
              onValueChange={(value) => {
                if (isSelectableRoundingMode(value)) {
                  handleRoundingModeChange(value);
                }
              }}
            >
              {selectableRoundingModeOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <Text
            data-testid={'risk-scoring-rounding-mode-explanation'}
            preset={'body-sm'}
            className={'text-neutral-muted-foreground'}
          >
            {explanation}
          </Text>
          {indication !== 'none' && (
            <Alert
              data-testid={'risk-scoring-recalculation-indication'}
              data-indication={indication}
              variant={indicationCopy.variant}
              size={'sm'}
            >
              {indication === 'failed' ? (
                <Alert.Status variant={'warning'} />
              ) : (
                <Alert.Info />
              )}
              <Alert.Header>
                <Alert.Title>{indicationCopy.title}</Alert.Title>
              </Alert.Header>
              <Alert.Description>
                {indicationCopy.description}
              </Alert.Description>
            </Alert>
          )}
          {matrixGaps.length > 0 && (
            <Alert
              data-testid={'risk-scoring-rounding-mode-gapped-matrix-warning'}
              variant={'warning'}
              size={'sm'}
            >
              <Alert.Status variant={'warning'} />
              <Alert.Header>
                <Alert.Title>
                  {t('RiskScoringRoundingMode_gapped_matrix_warning')}
                </Alert.Title>
              </Alert.Header>
              <Alert.Description>
                <ul className={'m-0 list-none p-0'}>
                  {matrixGaps.map(({ likelihood, impact }) => (
                    <li key={`${likelihood.value}:${impact.value}`}>
                      {`${likelihood.title} (${likelihood.value}) × ${impact.title} (${impact.value})`}
                    </li>
                  ))}
                </ul>
              </Alert.Description>
            </Alert>
          )}
        </div>
      )}
      <ControlledJsonEditor
        control={control}
        name={'RiskScoringModelConfig'}
        label={t('RiskScoringModelConfig')}
      />
      {isCustomerSupport && (
        <>
          <Button
            data-testid={'recalculate-risk-scores-button'}
            variant={'neutral'}
            style={'outline'}
            className={'w-fit'}
            onClick={() => setShowConfirmModal(true)}
            // Also blocked until the persisted mode has loaded: every
            // indication about a run reports the mode it started with
            // (RSP-7300), and starting with `storedRoundingMode` still
            // undefined would report the `nearest` fallback for an
            // organisation actually configured `up`, `down` or `none`. A
            // skipped query reports not-loading, so the models without a
            // rounding mode are unaffected.
            disabled={recalculating || aggregationSettingsLoading}
          >
            {recalculating && <Spinner />}
            {t('RecalculateRiskScores')}
          </Button>
          <RecalculateRiskScoresDialog
            open={showConfirmModal}
            organisationName={organisationName}
            onCancel={() => setShowConfirmModal(false)}
            onConfirm={handleRecalculate}
          />
        </>
      )}
    </div>
  );
};
